export enum MutationType {
  CREATE = "CREATE",
  DELETE = "DELETE",
  UPDATE = "UPDATE",
  // SUPER USER ONLY
  AUDIT = "AUDIT", // Can be used to track activity. Ex: user x opens Asset x
  LOGIN = "LOGIN",
  LOGIN_API = "LOGIN_API",
  LOGIN_ORGANIZATION = "LOGIN_ORGANIZATION",
}

type VisibleAssetTicketMutationSetting = {
  showAllTicketMutations: boolean
  showCreatedTicketMutations: boolean
  showDeletedTicketMutations: boolean
  showTicketStatusMutations: boolean
  showLinkedTicketMutations: boolean
}

export type VisibleAssetMutationSettings = { showAllAssetMutations: boolean } & VisibleAssetTicketMutationSetting

export interface MutationLogReference<PopulatedFields extends SubReferenceOptions = undefined> {
  assetCategory?: PopulatedFields extends "assetCategory" ? AssetCategory : string
  assetComponent?: PopulatedFields extends "assetComponent" ? AssetComponent : string
  linkedSupplier?: PopulatedFields extends "linkedSupplier" ? Organization : string
  assetComponentGroup?: PopulatedFields extends "assetComponentGroup" ? AssetComponentGroup : string
  assetValue?: PopulatedFields extends "assetValue" ? AssetValue : string
  externalKey?: PopulatedFields extends "externalKey" ? ExternalKey : string
  original?: PopulatedFields extends "original" ? unknown : string
  updated?: PopulatedFields extends "updated" ? unknown : string
}

export interface MutationLog<PopulatedSubReferences extends SubReferenceOptions = undefined> {
  _id: string
  type: MutationType
  organization?: string
  user?: { _id?: string; description: string }
  userOrganization?: { _id?: string; description: string }
  documentSchema?: string
  reference?: unknown
  subReference?: MutationLogReference<PopulatedSubReferences>
  description?: string
  prop?: string
  original?: string
  updated?: string
  createdAt: string
  asset?: string
  transactionId?: string
}

export type SubReferenceOptions = keyof MutationLogReference | undefined | unknown

export type MutationTransactionItem<PopulatedSubReferences extends SubReferenceOptions = unknown> = Pick<
  MutationLog<PopulatedSubReferences>,
  "prop" | "type" | "original" | "updated" | "subReference"
>

export type MutationTransaction<PopulatedSubReferences extends SubReferenceOptions = unknown> = Omit<
  MutationLog<PopulatedSubReferences>,
  "prop" | "original" | "updated" | "subReference"
> & {
  mutations: Array<MutationTransactionItem<PopulatedSubReferences>>
}

export const useMutationLogStore = defineStore("mutation-logs", () => {
  const settingsStore = useSettingsStore()

  const getMutationLogs = async <PopulatedSubReferences extends keyof MutationLogReference>(
    queryParameters?: Record<string, unknown>,
  ): Promise<Array<MutationLog<PopulatedSubReferences>>> => {
    const query = getUrlSearchParams({ ...queryParameters, pagination: false })

    const { data } = await useSamApi<PaginateResult<MutationLog<PopulatedSubReferences>>>(`/mutation-logs?${query}`)
    return data.docs
  }

  const getMutationLogsByPage = async <PopulatedSubReferences extends keyof MutationLogReference>(
    queryParameters: Record<string, unknown>,
  ) => {
    const query = getUrlSearchParams(queryParameters)

    const { data } = await useSamApi<PaginateResult<MutationLog<PopulatedSubReferences>>>(`/mutation-logs?${query}`)
    return data
  }

  const getComponentHistory = async (assetComponentId: string) => {
    const { componentHistoryEnabled } = storeToRefs(settingsStore)
    if (!componentHistoryEnabled) {
      return []
    }

    const queryObject = {
      pagination: false,
      query: JSON.stringify({
        documentSchema: "Asset",
        "subReference.assetComponent": assetComponentId,
      }),
      select: ["createdAt", "user", "userOrganization", "type", "prop", "original", "updated", "transactionId"],
      sort: JSON.stringify({ createdAt: "desc" }),
    }

    const query = getUrlSearchParams(queryObject)

    const { data } = await useSamApi<PaginateResult<MutationLog>>(`/mutation-logs?${query}`)
    return data.docs
  }

  const getComponentIdsWithHistoryForAsset = async (assetId: string) => {
    const { componentHistoryEnabled } = storeToRefs(settingsStore)
    if (!componentHistoryEnabled) {
      return []
    }

    const queryObject = {
      pagination: false,
      query: JSON.stringify({
        documentSchema: "Asset",
        reference: assetId,
        "subReference.assetComponent": { $exists: true },
      }),
      select: ["subReference.assetComponent", "subReference.assetComponentGroup"],
    }

    const query = getUrlSearchParams(queryObject)

    const { data } = await useSamApi<PaginateResult<MutationLog>>(`/mutation-logs?${query}`)

    // Extract unique asset component IDs from the logs
    const componentIds = new Set<string>()
    data.docs.forEach((log) => {
      if (log.subReference?.assetComponent) {
        componentIds.add(log.subReference.assetComponent)
      }
    })

    return Array.from(componentIds)
  }

  return {
    getMutationLogs,
    getMutationLogsByPage,
    getComponentHistory,
    /** Returns a list of component IDs that have mutations for the specific asset */
    getComponentIdsWithHistoryForAsset,
  }
})
